import { TYPES } from '../../render/constants';
import { deserializePoints, serializePoints } from '../../render/line';
import { Rect, Region } from '../region';
import type { RenderContext } from '../../render/context/createRenderContext';
import type { NodeType } from '../../render/context/node';
import type { HitTestContext } from '../../provider/interactive';
import { renderLockIndicator } from './label';

/** Line point in relative coordinates (0-1 range) */
interface LinePoint {
  x: number;
  y: number;
}

/** Line point handler interface */
interface LinePointHandler extends Rect {
  id: string;
  type: 'linePoint';
  pointIndex: number;
  relativePoint: LinePoint;
}

/** Line mid-point handler interface */
interface LineMidPointHandler extends Rect {
  id: string;
  type: 'lineMidPoint';
  segmentIndex: number;
  startPoint: LinePoint;
  endPoint: LinePoint;
  midPoint: LinePoint;
}

// Line节点的特殊点击检测
export function hitTestLineNode(
  stageContext: RenderContext,
  point: { x: number; y: number },
  node: NodeType
): boolean {
  const points = deserializePoints((node as any).points) as LinePoint[];
  if (!points || points.length < 2) return false;

  // 获取节点的旋转区域信息
  const region = stageContext.api.getNodeRegion(node);
  if (!region) return false;

  // 计算点击容差，考虑节点的size和stroke width
  const strokeWidth = (node as any).strokeWidth || 1;
  const nodeSize = (node as any).size || 4;
  const effectiveStrokeWidth = strokeWidth * nodeSize;

  // 基础容差根据有效描边宽度计算，确保点击区域合理
  const baseTolerancePixels = Math.max(effectiveStrokeWidth / 2, 3); // 至少3像素的点击区域

  // 计算线段的实际坐标
  const linePoints = points.map((point) => {
    // 基于region的四个角点，计算每个line point的实际位置
    const topLeft = region.topLeft;
    const topRight = region.topRight;
    const bottomLeft = region.bottomLeft;

    // 使用双线性插值计算旋转后的实际坐标
    const top = {
      x: topLeft.x + (topRight.x - topLeft.x) * point.x,
      y: topLeft.y + (topRight.y - topLeft.y) * point.x,
    };

    const bottom = {
      x: bottomLeft.x + (region.bottomRight.x - bottomLeft.x) * point.x,
      y: bottomLeft.y + (region.bottomRight.y - bottomLeft.y) * point.x,
    };

    const absoluteX = top.x + (bottom.x - top.x) * point.y;
    const absoluteY = top.y + (bottom.y - top.y) * point.y;

    return { x: absoluteX, y: absoluteY };
  });

  // 检查是否点击在线段附近
  for (let i = 0; i < linePoints.length - 1; i++) {
    const start = linePoints[i];
    const end = linePoints[i + 1];

    if (isPointNearLine(point, start, end, baseTolerancePixels)) {
      return true;
    }
  }
  return false;
}

// Line节点的特殊控制点处理
export function getInteractiveLineHandlers(
  _stageContext: RenderContext,
  node: NodeType,
  region: Region,
  scale = 1
): (LinePointHandler | LineMidPointHandler)[] {
  const points = deserializePoints((node as any).points) as LinePoint[];
  if (!points || points.length < 2) return [];

  const handlers: (LinePointHandler | LineMidPointHandler)[] = [];
  const controlPointSize = 12 * scale;

  // 为每个点创建控制点
  points.forEach((point, index) => {
    // 基于region的四个角点，计算每个line point的实际位置
    const topLeft = region.topLeft;
    const topRight = region.topRight;
    const bottomLeft = region.bottomLeft;

    // 使用双线性插值计算旋转后的实际坐标
    const top = {
      x: topLeft.x + (topRight.x - topLeft.x) * point.x,
      y: topLeft.y + (topRight.y - topLeft.y) * point.x,
    };

    const bottom = {
      x: bottomLeft.x + (region.bottomRight.x - bottomLeft.x) * point.x,
      y: bottomLeft.y + (region.bottomRight.y - bottomLeft.y) * point.x,
    };

    const absoluteX = top.x + (bottom.x - top.x) * point.y;
    const absoluteY = top.y + (bottom.y - top.y) * point.y;

    const rect = new Rect(
      absoluteX - controlPointSize,
      absoluteY - controlPointSize,
      controlPointSize * 2,
      controlPointSize * 2
    ) as LinePointHandler;

    rect.id = `linePoint_${index}`;
    rect.type = 'linePoint';
    rect.pointIndex = index;
    rect.relativePoint = point;

    handlers.push(rect);
  });

  // 添加线段中点控制点，用于快速加点
  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i];
    const end = points[i + 1];

    // 计算线段中点
    const midPoint = {
      x: (start.x + end.x) / 2,
      y: (start.y + end.y) / 2,
    };

    // 转换为绝对坐标
    const topLeft = region.topLeft;
    const topRight = region.topRight;
    const bottomLeft = region.bottomLeft;

    const top = {
      x: topLeft.x + (topRight.x - topLeft.x) * midPoint.x,
      y: topLeft.y + (topRight.y - topLeft.y) * midPoint.x,
    };

    const bottom = {
      x: bottomLeft.x + (region.bottomRight.x - bottomLeft.x) * midPoint.x,
      y: bottomLeft.y + (region.bottomRight.y - bottomLeft.y) * midPoint.x,
    };

    const absoluteX = top.x + (bottom.x - top.x) * midPoint.y;
    const absoluteY = top.y + (bottom.y - top.y) * midPoint.y;

    const rect = new Rect(
      absoluteX - controlPointSize * 0.6,
      absoluteY - controlPointSize * 0.6,
      controlPointSize * 1.2,
      controlPointSize * 1.2
    ) as LineMidPointHandler;

    rect.id = `lineMidPoint_${i}`;
    rect.type = 'lineMidPoint';
    rect.segmentIndex = i;
    rect.startPoint = start;
    rect.endPoint = end;
    rect.midPoint = midPoint;

    handlers.push(rect);
  }

  return handlers;
}

// Line节点的特殊边框渲染
export function renderInteractiveLineBorder(
  renderContext: RenderContext,
  context: CanvasRenderingContext2D,
  node: NodeType,
  _labelSize?: boolean,
  { lineWidth: _lineWidth = 4 }: { lineWidth?: number } = {}
): void {
  const { scale } = renderContext.providers;
  const region = renderContext.api
    .getNodeRegion(node)
    ?.transform(scale.scale, scale.translateX, scale.translateY);

  if (!region) return;

  const points = deserializePoints((node as any).points) as LinePoint[];
  if (!points || points.length < 2) return;

  // Check if node is locked
  const isLocked = renderContext.api.isNodeLocked(node);

  context.save();

  const zoom = scale.scale;
  const nodeLineWidth = ((node as any).size || 4) * zoom;
  const borderWidth = Math.max(6, nodeLineWidth + 4);

  // 创建线条路径函数
  const createLinePath = () => {
    context.beginPath();
    for (let i = 0; i < points.length; i++) {
      const point = points[i];

      // 基于region的四个角点，使用双线性插值计算旋转后的实际坐标
      const topLeft = region.topLeft;
      const topRight = region.topRight;
      const bottomLeft = region.bottomLeft;
      const bottomRight = region.bottomRight;

      // 使用双线性插值计算旋转后的实际坐标
      const top = {
        x: topLeft.x + (topRight.x - topLeft.x) * point.x,
        y: topLeft.y + (topRight.y - topLeft.y) * point.x,
      };

      const bottom = {
        x: bottomLeft.x + (bottomRight.x - bottomLeft.x) * point.x,
        y: bottomLeft.y + (bottomRight.y - bottomLeft.y) * point.x,
      };

      const x = top.x + (bottom.x - top.x) * point.y;
      const y = top.y + (bottom.y - top.y) * point.y;

      if (i === 0) {
        context.moveTo(x, y);
      } else {
        context.lineTo(x, y);
      }
    }
  };

  // 绘制外层边框 - 使用不同颜色标识锁定状态
  context.strokeStyle = isLocked ? 'rgba(245, 158, 11, 0.5)' : 'rgba(139, 62, 255, 0.5)';
  context.lineWidth = borderWidth;
  context.lineCap = 'round';
  context.lineJoin = 'round';

  createLinePath();
  context.stroke();

  context.restore();

  // Render lock indicator if node is locked
  if (isLocked) {
    renderLockIndicator(context, region);
  }
}

// 检查点是否在线段附近
function isPointNearLine(
  point: { x: number; y: number },
  lineStart: { x: number; y: number },
  lineEnd: { x: number; y: number },
  tolerance: number
): boolean {
  const A = point.x - lineStart.x;
  const B = point.y - lineStart.y;
  const C = lineEnd.x - lineStart.x;
  const D = lineEnd.y - lineStart.y;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;

  if (lenSq === 0) return false;

  const param = dot / lenSq;

  let xx, yy;

  if (param < 0) {
    xx = lineStart.x;
    yy = lineStart.y;
  } else if (param > 1) {
    xx = lineEnd.x;
    yy = lineEnd.y;
  } else {
    xx = lineStart.x + param * C;
    yy = lineStart.y + param * D;
  }

  const dx = point.x - xx;
  const dy = point.y - yy;
  const distance = Math.sqrt(dx * dx + dy * dy);

  return distance <= tolerance;
}

// Line节点双击处理 - 添加新点
export function handleLineDoubleClick(hitTestContext: HitTestContext): boolean {
  const node = hitTestContext.targetNode || hitTestContext.shallowNode;
  const stageContext = hitTestContext.stageContext;

  if (!node || node.type !== TYPES.line) return false;

  const points = deserializePoints((node as any).points) as LinePoint[];
  if (!points || points.length < 2) return false;

  // 转换点击坐标到Line节点的相对坐标系
  const region = stageContext.api.getNodeRegion(node);
  if (!region) return false;
  const relativePoint = {
    x: (hitTestContext.point.x - region.rect.x) / region.rect.w,
    y: (hitTestContext.point.y - region.rect.y) / region.rect.h,
  };

  // 找到最近的线段
  let minDistance = Infinity;
  let insertIndex = -1;

  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i];
    const end = points[i + 1];

    const distance = distanceToLineSegment(relativePoint, start, end);
    if (distance < minDistance) {
      minDistance = distance;
      insertIndex = i + 1;
    }
  }

  // 如果距离足够近，在最近的位置插入新点
  if (minDistance < 0.1 && insertIndex > 0) {
    const newPoints = [...points];
    newPoints.splice(insertIndex, 0, {
      x: Math.max(0, Math.min(1, relativePoint.x)),
      y: Math.max(0, Math.min(1, relativePoint.y)),
    });

    stageContext.api.updateNode(node.id, {
      points: serializePoints(newPoints),
    } as any);

    return true;
  }

  return false;
}

// 计算点到线段的距离
function distanceToLineSegment(point: LinePoint, lineStart: LinePoint, lineEnd: LinePoint): number {
  const A = point.x - lineStart.x;
  const B = point.y - lineStart.y;
  const C = lineEnd.x - lineStart.x;
  const D = lineEnd.y - lineStart.y;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;

  if (lenSq === 0) {
    // 线段退化为点
    const dx = point.x - lineStart.x;
    const dy = point.y - lineStart.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  const param = dot / lenSq;

  let xx, yy;

  if (param < 0) {
    xx = lineStart.x;
    yy = lineStart.y;
  } else if (param > 1) {
    xx = lineEnd.x;
    yy = lineEnd.y;
  } else {
    xx = lineStart.x + param * C;
    yy = lineStart.y + param * D;
  }

  const dx = point.x - xx;
  const dy = point.y - yy;
  return Math.sqrt(dx * dx + dy * dy);
}

// 在指定线段添加点
export function addPointAtSegment(
  hitTestContext: HitTestContext,
  segmentIndex: number | null = null
): boolean {
  const node = hitTestContext.targetNode || hitTestContext.shallowNode;
  const stageContext = hitTestContext.stageContext;

  if (!node || node.type !== TYPES.line) return false;

  const points = deserializePoints((node as any).points) as LinePoint[];
  if (!points || points.length < 2) return false;

  let targetSegmentIndex = segmentIndex;

  // 如果没有指定segmentIndex，从handler中获取
  if (
    targetSegmentIndex === null &&
    hitTestContext.targetHandler &&
    hitTestContext.targetHandler.type === 'lineMidPoint'
  ) {
    targetSegmentIndex = hitTestContext.targetHandler.segmentIndex;
  }

  if (
    targetSegmentIndex === null ||
    targetSegmentIndex < 0 ||
    targetSegmentIndex >= points.length - 1
  ) {
    return false;
  }

  const start = points[targetSegmentIndex];
  const end = points[targetSegmentIndex + 1];

  // 在线段中点添加新点
  const newPoint = {
    x: (start.x + end.x) / 2,
    y: (start.y + end.y) / 2,
  };

  const newPoints = [...points];
  newPoints.splice(targetSegmentIndex + 1, 0, newPoint);

  stageContext.api.updateNode(node.id, {
    points: serializePoints(newPoints),
  } as any);

  return true;
}
